/**
 * The Vendors overview + vendor-detail screens. A view module (app.tsx split, HS2-ZB7GJN).
 */
import './vendors-view.css';

import { Button } from '@kerfjs/ui/button';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { type SafeHtml } from 'kerfjs';

import { EdgeToEdgeTable } from '../components/edge-to-edge-table.js';
import { PageLoading } from '../components/page-loading.js';
import { notice, shellSearch, vendorDetail, vendorDialog, vendorsOverview } from '../data/state.js';
import { matchesSettingsSearch } from '../lib/settings-search.js';
import { VendorDetailView } from './vendors/vendor-detail-view.js';
import { VendorDialog } from './vendors/vendor-dialog.js';

export function VendorsView(): SafeHtml {
  const detail = vendorDetail.value;
  const dialog = vendorDialog.value;
  // Add from the list header or edit from a vendor's header, in one dialog (HS2-BBZXKM).
  const vendorEditor =
    dialog === null ? null : (
      <VendorDialog
        vendor={dialog.vendorId !== null && detail?.vendor.id === dialog.vendorId ? detail.vendor : undefined}
        error={dialog.error}
      />
    );
  if (detail !== null)
    return (
      <>
        <VendorDetailView detail={detail} />
        {vendorEditor}
      </>
    );
  const o = vendorsOverview.value;
  const filtered = o?.vendors.filter((vendor) =>
    matchesSettingsSearch([vendor.name, vendor.type, vendor.city, vendor.country], shellSearch.value),
  );
  const duplicateGroups = o?.duplicateGroups.filter((group) =>
    matchesSettingsSearch(group.vendorNames, shellSearch.value),
  );
  return (
    <section class="vendors-view" data-page-loading={o === null ? '' : undefined} aria-label="Vendors">
      {o === null ? (
        <PageLoading label="Loading vendors" />
      ) : (
        <>
          {duplicateGroups !== undefined && duplicateGroups.length > 0 ? (
            <List gap="xs">
              {duplicateGroups.map((g) => (
                <div class="dup-banner" data-key={g.key}>
                  <StateBanner
                    tone="warning"
                    title="Possible Duplicate Vendors"
                    detail={g.vendorNames.join(' · ')}
                    copyLayout="stacked"
                    actionPlacement="below"
                    action={
                      <Button
                        tone="neutral"
                        emphasis="primary"
                        size="compact"
                        rootAttributes={{
                          'data-vendor-merge': `${g.vendorIds.slice(1).join(',')}:${g.vendorIds[0] ?? ''}`,
                        }}
                      >
                        Merge
                      </Button>
                    }
                  />
                </div>
              ))}
            </List>
          ) : null}
          {filtered?.length === 0 ? (
            <EmptyState
              title={shellSearch.value.trim() === '' ? 'No vendors yet' : 'No matching vendors'}
              detail={
                shellSearch.value.trim() === ''
                  ? 'They are created when you add quotations, or add one with New Vendor….'
                  : 'Try another search.'
              }
            />
          ) : (
            <EdgeToEdgeTable label="Vendors Table">
              <table class="t vendors-grid">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Type</th>
                    <th>City</th>
                    <th class="num">Quotes</th>
                    <th class="num">Purchases</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map((v) => (
                    <tr data-key={v.id}>
                      <td>
                        <Button
                          emphasis="plain"
                          tone="brand"
                          size="compact"
                          rootAttributes={{ 'data-vendor-open': v.id }}
                        >
                          {v.name}
                        </Button>
                      </td>
                      <td>{v.type ?? '—'}</td>
                      <td>{v.city ?? '—'}</td>
                      <td class="num">{String(v.quoteCount)}</td>
                      <td class="num">{String(v.outcomeCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </EdgeToEdgeTable>
          )}
        </>
      )}
      {notice.value !== '' ? <StateBanner tone="info" title={notice.value} /> : null}
      {vendorEditor}
    </section>
  );
}

export { VendorDetailView } from './vendors/vendor-detail-view.js';
